import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { Icon } from './JournalOverview';

const dismissedThisSession = new Set();
export const releaseKey = (uid, version) => `tradelog-release:${uid}:${version}`;
const locallyDismissed = key => {
  if (dismissedThisSession.has(key)) return true;
  try { return localStorage.getItem(key) === 'dismissed'; } catch { return false; }
};
const releaseRef = (uid, version) => doc(db, 'users', uid, 'meta', `release-${version.replaceAll('.', '-')}`);

export function useReleaseNotice(uid, version) {
  const key = uid ? releaseKey(uid, version) : '';
  const [visibleKey, setVisibleKey] = useState('');
  useEffect(() => {
    let active = true;
    setVisibleKey('');
    if (!uid) return;
    const sync = () => setVisibleKey(locallyDismissed(key) ? '' : key);
    const read = async () => {
      if (locallyDismissed(key)) return;
      try {
        const snapshot = await getDoc(releaseRef(uid, version));
        if (!active) return;
        if (snapshot.exists() && snapshot.data().dismissed) {
          dismissedThisSession.add(key);
          try { localStorage.setItem(key, 'dismissed'); } catch {}
        }
      } catch { /* Keep the announcement available when the network is offline. */ }
      if (active) sync();
    };
    read();
    const storage = event => { if (event.key === key) sync(); };
    window.addEventListener('storage', storage);
    return () => { active = false; window.removeEventListener('storage', storage); };
  }, [key, uid, version]);
  const dismiss = useCallback(() => {
    if (!uid) return;
    dismissedThisSession.add(key); setVisibleKey('');
    try { localStorage.setItem(key, 'dismissed'); } catch {}
    // Local dismissal is immediate; signed-in accounts also remember it on other devices.
    setDoc(releaseRef(uid, version), { dismissed: true, version, dismissedAt: Date.now() }, { merge: true }).catch(() => {});
  }, [key, uid, version]);
  return { visible: !!key && visibleKey === key, dismiss };
}

export default function ReleaseNotice({ version, onOpen, onDismiss, title='The details make a difference.', description='Safer profile privacy, more dependable analysis history, and clearer support and exports.' }) {
  return <aside className="tl-release-notice" aria-label={`TradeLog ${version} update`}>
    <span className="tl-release-emblem"><Icon name="sparkles" size={21}/></span>
    <div><span className="tl-eyebrow">NEW IN TRADELOG · {version}</span><strong>{title}</strong><p>{description}</p></div>
    <button className="tl-btn" onClick={onOpen}>See what’s new <Icon name="arrow" size={15}/></button>
    <button className="tl-icon-button" aria-label="Dismiss this update" onClick={onDismiss}><Icon name="close" size={17}/></button>
  </aside>;
}
